import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, thumbUrl, mediaUrl } from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import { TopBar } from '@/components/TopBar';
import { EventCard, TYPE_LABELS } from '@/components/EventCard';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Plus, Heart, Compass, Bookmark, Camera, Image as ImageIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { MasonryGrid } from '@/components/MasonryGrid';
import { MediaLightbox } from '@/components/MediaLightbox';

export default function DashboardPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [evR, stR, fdR] = await Promise.all([
          api.get('/events?scope=mine'),
          api.get('/stats/me'),
          api.get('/me/feed?limit=24'),
        ]);
        if (!mounted) return;
        setEvents(evR.data || []);
        setStats(stR.data || null);
        setFeed(fdR.data || []);
      } catch (e) {
        toast.error('Failed to load dashboard');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const featured = useMemo(() => events.find((e) => e.status === 'ongoing') || events.find((e) => e.status === 'upcoming') || events[0], [events]);
  const upcoming = useMemo(() => events.filter((e) => e.status !== 'past').slice(0, 3), [events]);
  const greeting = useMemo(() => greetingFor(new Date()), []);

  return (
    <div data-testid="dashboard-page">
      <TopBar
        title={`${greeting},`}
        subtitle={user?.name?.split(' ')?.[0] || 'Traveler'}
        user={user}
      />

      {/* Stats bento */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-3 gap-2 mb-5" data-testid="dashboard-stats">
        <StatTile icon={Compass} label="Trips" value={stats?.owned_events ?? 0} testid="stat-trips" />
        <StatTile icon={Camera} label="Media" value={stats?.uploaded_media ?? 0} testid="stat-media" />
        <StatTile icon={Bookmark} label="Bucket" value={stats?.bucket_items ?? 0} testid="stat-bucket" />
      </motion.div>

      {/* Featured */}
      {featured ? (
        <FeaturedEvent event={featured} />
      ) : !loading ? (
        <EmptyState
          title="Your story starts here"
          description="Plan a trip, conference, or get-together. Memoir keeps it private and beautifully organized."
          action={
            <Button data-testid="dashboard-create-cta" onClick={() => navigate('/events/new')} className="rounded-xl gradient-fab text-white px-5 h-11">
              <Plus className="h-4 w-4 mr-2" /> Create event
            </Button>
          }
        />
      ) : null}

      {/* Recent Media feed */}
      <section className="mt-7" data-testid="dashboard-recent-media">
        <SectionHeader title="Recent media" linkTo="/memories" />
        {feed.length === 0 ? (
          <Card className="rounded-2xl p-6 text-center" data-testid="recent-media-empty">
            <ImageIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <div className="text-sm text-muted-foreground">No media yet. Upload photos to an event to populate this feed.</div>
          </Card>
        ) : (
          <MasonryGrid testid="recent-media-grid">
            {feed.map((m, idx) => (
              <button key={m.id} onClick={() => setLightboxIdx(idx)} data-testid={`feed-media-${m.id}`} className="block w-full overflow-hidden rounded-xl bg-secondary relative">
                <div style={{ aspectRatio: `${Math.max(0.5, Math.min(1.8, (m.width && m.height) ? m.width / m.height : 1))}` }}>
                  {m.is_video ? (
                    <video src={mediaUrl(m.id)} preload="metadata" className="w-full h-full object-cover" />
                  ) : (
                    <img src={thumbUrl(m.id)} alt="" loading="lazy" className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="absolute bottom-0 inset-x-0 px-2 py-1 bg-gradient-to-t from-black/55 to-transparent text-white text-[10px] truncate text-left">{m.event_title}</div>
              </button>
            ))}
          </MasonryGrid>
        )}
      </section>

      {/* Upcoming list */}
      {upcoming.length > 0 && (
        <section className="mt-6" data-testid="dashboard-upcoming">
          <SectionHeader title="Upcoming" linkTo="/events" />
          <div className="grid grid-cols-1 gap-3">
            {upcoming.map((ev) => (
              <EventCard key={ev.id} event={ev} />
            ))}
          </div>
        </section>
      )}

      <MediaLightbox open={lightboxIdx >= 0} indexInitial={lightboxIdx} items={feed} onClose={() => setLightboxIdx(-1)} onUpdated={(u, i) => setFeed((arr) => arr.map((x, j) => j === i ? u : x))} />
    </div>
  );
}

const StatTile = ({ icon: Icon, label, value, testid }) => (
  <Card data-testid={testid} className="p-3 rounded-2xl">
    <div className="flex items-center gap-2 text-muted-foreground text-[11px] uppercase tracking-wider">
      <Icon className="h-3.5 w-3.5" />
      <span>{label}</span>
    </div>
    <div className="font-semibold text-2xl mt-1">{value}</div>
  </Card>
);

const FeaturedEvent = ({ event }) => {
  const navigate = useNavigate();
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="dashboard-featured-event" onClick={() => navigate(`/events/${event.id}`)} className="cursor-pointer">
      <div className="relative rounded-2xl overflow-hidden h-44 sm:h-56 border border-border">
        {event.cover_image ? <img src={event.cover_image} alt="" className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 gradient-signature" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-white/95 bg-black/35 backdrop-blur px-2 py-1 rounded-full">{TYPE_LABELS[event.type] || 'Event'}</span>
          <span className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--primary))] bg-white px-2 py-1 rounded-full">Featured</span>
        </div>
        <div className="absolute bottom-4 left-4 right-4 text-white">
          <div className="font-serif text-2xl sm:text-3xl font-semibold leading-tight line-clamp-2">{event.title}</div>
          <div className="mt-1 text-xs sm:text-sm opacity-90">{event.location_text || 'Plan your itinerary'}</div>
        </div>
      </div>
    </motion.div>
  );
};

const SectionHeader = ({ title, linkTo }) => (
  <div className="flex items-center justify-between mb-3">
    <h2 className="font-serif text-xl font-semibold">{title}</h2>
    {linkTo && <Link to={linkTo} className="text-sm font-medium text-[hsl(var(--primary))] hover:underline">View all</Link>}
  </div>
);

function greetingFor(d) {
  const h = d.getHours();
  if (h < 5) return 'Good night';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 21) return 'Good evening';
  return 'Good night';
}
